"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { signIn } from "next-auth/react"
import { useRouter } from "next/navigation"
import {
  ArrowRight,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Sparkles,
  UserRound,
} from "lucide-react"
import { Header } from "@/components/layout/header"
import { TurnstileWidget } from "@/components/security/turnstile-widget"
import styles from "./register-page.module.css"

export default function RegisterPage() {
  const router = useRouter()
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)

  const resetVerification = () => {
    setTurnstileToken("")
    setTurnstileResetKey((key) => key + 1)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")
    setLoading(true)

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email,
          password,
          turnstileToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || "Something went wrong. Please try again.")
        resetVerification()
        return
      }

      const result = await signIn("credentials", {
        email,
        password,
        registrationGrant: data.registrationGrant,
        redirect: false,
      })

      if (result?.error) {
        setError("Registration succeeded, but sign in failed")
        resetVerification()
      } else {
        router.push("/play")
        router.refresh()
      }
    } catch {
      setError("Something went wrong. Please try again.")
      resetVerification()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`public-site ${styles.page}`}>
      <Header />

      <main className={styles.stage}>
        <section className={styles.registrationShell}>
          <div className={styles.heroPanel}>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}>
                <Sparkles aria-hidden="true" />
                Join the streak
              </p>
              <h1>
                Create an account.
                <span>Start <em>guessing.</em></span>
              </h1>
              <p className={styles.heroDescription}>
                Save your best runs, play the daily challenge, and climb the
                leaderboard.
              </p>
            </div>

            <div className={styles.heroArtwork} aria-hidden="true">
              <span className={styles.artworkGlow} />
              <Image
                src="/images/auth/register-passport.webp"
                alt=""
                width={964}
                height={767}
                sizes="(max-width: 620px) 100vw, (max-width: 980px) 42vw, 620px"
                priority
              />
            </div>
          </div>

          <div className={styles.formPanel}>
            <div className={styles.formContent}>
              <header className={styles.formHeader}>
                <h2>Register</h2>
                <p>
                  Already have an account? <Link href="/login">Sign in</Link>
                </p>
              </header>

              <form onSubmit={handleSubmit} className={styles.form}>
                {error ? (
                  <div role="alert" className={styles.errorMessage}>
                    {error}
                  </div>
                ) : null}

                <div className={styles.field}>
                  <div className={styles.labelRow}>
                    <label htmlFor="name">Display name</label>
                    <span>Optional</span>
                  </div>
                  <div className={styles.inputShell}>
                    <UserRound aria-hidden="true" />
                    <input
                      id="name"
                      type="text"
                      placeholder="Choose a display name"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      autoComplete="name"
                      maxLength={50}
                    />
                  </div>
                </div>

                <div className={styles.field}>
                  <label htmlFor="email">Email</label>
                  <div className={styles.inputShell}>
                    <Mail aria-hidden="true" />
                    <input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      autoComplete="email"
                      required
                    />
                  </div>
                </div>

                <div className={styles.field}>
                  <label htmlFor="password">Password</label>
                  <div className={styles.inputShell}>
                    <LockKeyhole aria-hidden="true" />
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="new-password"
                      required
                      maxLength={128}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                    >
                      {showPassword ? (
                        <EyeOff aria-hidden="true" />
                      ) : (
                        <Eye aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <div className={styles.turnstileWrap}>
                  <TurnstileWidget
                    onTokenChange={setTurnstileToken}
                    resetKey={turnstileResetKey}
                    theme="light"
                  />
                </div>

                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={loading || !turnstileToken}
                >
                  <span>
                    {loading ? "Creating account..." : "Create account"}
                  </span>
                  <i aria-hidden="true">
                    {loading ? (
                      <LoaderCircle className={styles.spinner} />
                    ) : (
                      <ArrowRight />
                    )}
                  </i>
                </button>
              </form>

              <p className={styles.formNote}>
                Free account. No payment details required. By creating an
                account, you agree to the{" "}
                <Link href="/terms">Terms of Service</Link> and acknowledge the{" "}
                <Link href="/privacy">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
